// Option arrays + labels backing the trigger condition editor selects. Values
// are the UI string tokens consumed by build-trigger-condition.ts.
import { TriggerConditionType } from '@/types/generated/triggers'

import type {
  CandleFieldToken,
  ConditionKind,
  IndicatorTypeToken,
  OperatorToken,
  OptionIvSourceToken,
  OptionTypeToken,
  PortfolioMetricToken,
  PositionMetricToken,
  PositionScopeKindToken,
  PriceSourceToken,
  RiskMetricToken,
} from './trigger-condition-model'

export type Option<T extends string> = { value: T; label: string }

export const OPERATOR_OPTIONS: Option<OperatorToken>[] = [
  { value: 'equal_to', label: '= Equal to' },
  { value: 'not_equal', label: '≠ Not equal' },
  { value: 'greater_than', label: '> Greater than' },
  { value: 'less_than', label: '< Less than' },
  { value: 'greater_than_or_equal', label: '≥ Greater or equal' },
  { value: 'less_than_or_equal', label: '≤ Less or equal' },
  { value: 'cross_above', label: '↗ Crosses above' },
  { value: 'cross_below', label: '↘ Crosses below' },
]

export const PRICE_SOURCE_OPTIONS: Option<PriceSourceToken>[] = [
  { value: 'mark', label: 'Mark' },
  { value: 'last', label: 'Last' },
  { value: 'index', label: 'Index' },
  { value: 'mid', label: 'Mid' },
  { value: 'best_bid', label: 'Best Bid' },
  { value: 'best_ask', label: 'Best Ask' },
]

export const INDICATOR_TYPE_OPTIONS: Option<IndicatorTypeToken>[] = [
  { value: 'sma', label: 'SMA' },
  { value: 'ema', label: 'EMA' },
]

export const CANDLE_FIELD_OPTIONS: Option<CandleFieldToken>[] = [
  { value: 'open', label: 'Open' },
  { value: 'high', label: 'High' },
  { value: 'low', label: 'Low' },
  { value: 'close', label: 'Close' },
  { value: 'vwap', label: 'VWAP' },
  { value: 'twap', label: 'TWAP' },
  { value: 'volume_base', label: 'Volume (base)' },
  { value: 'volume_quote', label: 'Volume (quote)' },
]

export const PORTFOLIO_METRIC_OPTIONS: Option<PortfolioMetricToken>[] = [
  { value: 'account_value_usd', label: 'Account value (USD)' },
  { value: 'currency_balance', label: 'Currency balance' },
]

export const POSITION_METRIC_OPTIONS: Option<PositionMetricToken>[] = [
  { value: 'quantity', label: 'Quantity' },
  { value: 'mark_price', label: 'Mark price' },
  { value: 'unrealized_pnl', label: 'Unrealized PnL' },
  { value: 'realized_pnl', label: 'Realized PnL' },
  { value: 'daily_unrealized_pnl', label: 'Daily unrealized PnL' },
  { value: 'initial_margin', label: 'Initial margin' },
  { value: 'maintenance_margin', label: 'Maintenance margin' },
  { value: 'delta', label: 'Delta' },
  { value: 'gamma', label: 'Gamma' },
  { value: 'vega', label: 'Vega' },
  { value: 'theta', label: 'Theta' },
  { value: 'rho', label: 'Rho' },
  { value: 'delta_usd', label: 'Delta (USD)' },
  { value: 'gamma_usd', label: 'Gamma (USD)' },
  { value: 'vega_usd', label: 'Vega (USD)' },
  { value: 'theta_usd', label: 'Theta (USD)' },
  { value: 'unrealized_pnl_usd', label: 'Unrealized PnL (USD)' },
  { value: 'realized_pnl_usd', label: 'Realized PnL (USD)' },
  { value: 'daily_unrealized_pnl_usd', label: 'Daily unrealized PnL (USD)' },
  { value: 'initial_margin_usd', label: 'Initial margin (USD)' },
  { value: 'maintenance_margin_usd', label: 'Maintenance margin (USD)' },
]

export const RISK_METRIC_OPTIONS: Option<RiskMetricToken>[] = [
  { value: 'pnl', label: 'PnL' },
  { value: 'delta', label: 'Delta' },
  { value: 'gamma', label: 'Gamma' },
  { value: 'vega', label: 'Vega' },
  { value: 'theta', label: 'Theta' },
  { value: 'rho', label: 'Rho' },
  { value: 'vanna', label: 'Vanna' },
  { value: 'volga', label: 'Volga' },
  { value: 'weighted_vega', label: 'Weighted vega' },
]

export const OPTION_IV_SOURCE_OPTIONS: Option<OptionIvSourceToken>[] = [
  { value: 'bid_iv', label: 'Bid IV' },
  { value: 'ask_iv', label: 'Ask IV' },
  { value: 'mark_iv', label: 'Mark IV' },
  { value: 'mid_iv', label: 'Mid IV' },
]

export const OPTION_TYPE_OPTIONS: Option<OptionTypeToken>[] = [
  { value: 'call', label: 'Call' },
  { value: 'put', label: 'Put' },
]

export const POSITION_SCOPE_OPTIONS: Option<PositionScopeKindToken>[] = [
  { value: 'symbol', label: 'Symbol' },
  { value: 'underlying', label: 'Underlying' },
  { value: 'group', label: 'Position group' },
  { value: 'all', label: 'All positions' },
]

/** Selectable condition kinds (action_event is internal/legacy, so excluded). */
export type SelectableConditionKind = Exclude<ConditionKind, 'action_event'>

/** Leaf families selectable in the editor (action_event is internal/legacy). */
export const CONDITION_KIND_OPTIONS: Option<SelectableConditionKind>[] = [
  { value: 'price', label: 'Price' },
  { value: 'candle', label: 'Candle' },
  { value: 'portfolio_metric', label: 'Portfolio metric' },
  { value: 'position_stats', label: 'Position stats' },
  { value: 'risk_metric', label: 'Risk metric' },
  { value: 'option_iv', label: 'Option IV' },
  { value: 'schedule', label: 'Schedule' },
  { value: 'db_news', label: 'News' },
]

/**
 * Maps each selectable editor kind to the proto `TriggerConditionType` the
 * backend gates on, so the plan-capability check (`allowedTriggerConditions`)
 * can disable the kinds a tier can't use (FREE = PRICE only). The combinators
 * AND/OR are themselves gated conditions — a tier restricted to a single price
 * trigger can't group.
 */
export const CONDITION_KIND_TO_TRIGGER_TYPE: Record<SelectableConditionKind, TriggerConditionType> =
  {
    price: TriggerConditionType.TRIGGER_CONDITION_TYPE_PRICE,
    technical_indicator: TriggerConditionType.TRIGGER_CONDITION_TYPE_TECHNICAL_INDICATOR,
    returns: TriggerConditionType.TRIGGER_CONDITION_TYPE_RETURNS,
    candle: TriggerConditionType.TRIGGER_CONDITION_TYPE_CANDLE,
    portfolio_metric: TriggerConditionType.TRIGGER_CONDITION_TYPE_PORTFOLIO_METRIC,
    position_stats: TriggerConditionType.TRIGGER_CONDITION_TYPE_POSITION_METRIC,
    risk_metric: TriggerConditionType.TRIGGER_CONDITION_TYPE_RISK_METRIC,
    option_iv: TriggerConditionType.TRIGGER_CONDITION_TYPE_OPTION_IV,
    schedule: TriggerConditionType.TRIGGER_CONDITION_TYPE_SCHEDULE,
    db_news: TriggerConditionType.TRIGGER_CONDITION_TYPE_DB_NEWS,
    and: TriggerConditionType.TRIGGER_CONDITION_TYPE_AND,
    or: TriggerConditionType.TRIGGER_CONDITION_TYPE_OR,
  }

/** Cross-above / cross-below explainer copy (tooltip). */
export const CROSS_ABOVE_HELP =
  'Cross above fires only when the previous sample was ≤ the target and the current sample is > the target. If the value is already above when armed, it does not immediately fire.'
export const CROSS_BELOW_HELP =
  'Cross below fires only when the previous sample was ≥ the target and the current sample is < the target. If the value is already below when armed, it does not immediately fire.'
